'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';

import useTranslation from '@components/Translation/useTranslation';
import { useUIListener } from '@repo/ui/src/modules/UI/hooks/useUIListener';
import { useDelayedCB } from '@hooks/useDelayedCB';
import { ModalID, useDrawer, useModal } from '@repo/ui/src/modules/UI';
import { trackCart } from '@functions/monetate/cart';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useBasket from '@hooks/basket/useBasket';
import Auth from '@components/Auth';
import useGoToCheckout from '@hooks/basket/useGoToCheckout';
import { useCustomer } from '@hooks/customer';
import { DrawerHeader } from '@repo/ui/src/modules/UI/components/Drawer/_DrawerHeader';

import { logCartViewed } from './helpers';
import CartBody from './_CartBody';
import CartFooter from './_CartFooter';

const CartRecommendations = dynamic(() => import('@components/Cart/CartRecommendations'));

const Cart = () => {
  const { close } = useDrawer();
  const { push: pushCheckoutLoader } = useModal(ModalID.checkoutLoader);
  const { data: basket, isLoading } = useBasket();
  const { trigger: goToCheckout, error, isMutating } = useGoToCheckout();
  const { data: user } = useCustomer();
  const { t } = useTranslation();
  const { locale, push } = useCurrentUrl();
  const showRecommendations = locale === 'en-US';
  const [isAuthStep, setIsAuthStep] = useState(false);

  const navigateToCheckout = () => {
    pushCheckoutLoader();
    close();
    push(`/${locale}/checkout`);
  };
  const outOfStockItems = error?.info;

  const skus = basket?.lineItems?.map((lineItem) => lineItem.sku);

  const disabled = outOfStockItems?.some((element: string) => skus?.includes(element));

  const { lineItems = [], totalItems = 0 } = basket || {};
  const isEmpty = !totalItems;

  const sendMonetate = React.useCallback(() => {
    trackCart(basket);
  }, [basket]);

  React.useEffect(() => {
    if (basket?.totalItems || basket?.giftCardTotal) {
      sendMonetate();
    }
  }, [lineItems, basket, sendMonetate]);

  const cb = useDelayedCB(
    (isOpen: boolean) => {
      if (basket) {
        logCartViewed(
          `${isOpen ? 'open' : 'close'} cart`,
          basket,
          lineItems,
          basket.recommendations,
        );
      }
      if (!isOpen) {
        setTimeout(() => setIsAuthStep(false), 500);
      }
    },
    React.useCallback(() => !isLoading, [isLoading]),
    { maxTries: 30 },
  );

  useUIListener(ModalID.cart, cb);

  const continueToCheckout = React.useCallback(async () => {
    try {
      // if there's no user wait until the auth step is finished to create session
      // in case the user logs in
      // this throws an error if there are out stock items skipping the next steps
      await goToCheckout({ skipSession: !user });

      if (user) {
        navigateToCheckout();
      } else {
        setIsAuthStep(true);
      }
    } catch {
      /* empty */
    }
  }, [goToCheckout, user, locale]);

  const loggedOutTitle = t('cart.loggedOutTitle', undefined, { default: 'none' });
  const loggedOutSentence = t('cart.loggedOutSentence', undefined, { default: 'none' });

  return (
    <div className="tw-pb-28 tw-grid tw-grid-cols-1 tw-justify-center tw-min-h-screen tw-justify-items-stretch tw-grid-rows-[1fr_auto] md:tw-items-stretch md:tw-grid-areas-cartDrawer md:tw-grid-cols-[auto_auto] md:tw-pb-0">
      <div className="tw-full tw-flex tw-flex-col tw-flex-grow sm:tw-w-[600px] md:tw-grid-in-b">
        {isAuthStep ? (
          <Auth
            onSuccess={async () => {
              try {
                // verification was done before auth step
                // this throws an error if there are out stock items skipping the next steps
                await goToCheckout({ skipVerification: true });
                navigateToCheckout();
              } catch {
                /* empty */
              }
            }}
            isCheckout
          />
        ) : (
          <>
            <DrawerHeader title={t('cart.title', undefined, { default: 'Your Cart' })} />
            <div className="tw-flex tw-justify-between tw-flex-col">
              <div className="tw-px-6 tw-mb-5 sm:tw-px-16 tw-mx-auto tw-max-w-3xl tw-w-full">
                {loggedOutTitle !== 'none' && loggedOutSentence !== 'none' && (
                  <>
                    <h6 className="tra-text-web-heading-6 tw-font-normal tw-text-left tw-pb-6">
                      {loggedOutTitle}
                    </h6>
                    <p>{loggedOutSentence}</p>
                  </>
                )}
                <hr className="tw-hidden sm:tw-block tw-w-full tw-border-surface-200" />
                <CartBody isEmpty={isEmpty} outOfStockItems={outOfStockItems} />
              </div>
            </div>
          </>
        )}
      </div>
      {showRecommendations && !isAuthStep && <CartRecommendations />}
      {!isAuthStep && (
        <CartFooter
          isLoading={isLoading}
          isEmpty={isEmpty}
          continueToCheckout={continueToCheckout}
          disabled={disabled}
          loadingCheckout={isMutating}
        />
      )}
    </div>
  );
};

export default Cart;
